import videojs from 'video.js'
import throttle from 'lodash/throttle.js'
import { revealElement, hideElement, registerPluginOnce } from '../utils.js'
import { CustomEvents } from '../types/index.ts'

const Plugin = videojs.getPlugin('plugin')

// Default options for the plugin.
const defaults = {
  endingCreditsTimecode: Number
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBNextEpisode extends Plugin {
  /**
   * Create a NFBNextEpisode plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.options = videojs.obj.merge(defaults, options)

    this.playerEl = this.player.el()
    this.controlPrefix = `${this.player.id()}-`
    this.els = {
      nextEpisodeContainer: null
    }
    this.wasForcePausedBy = null
    this.isVisible = false

    this.player.ready(() => {
      this.init()
    })
  }

  init () {
    this.log('Initializing NFB Next Episode plugin')
    this.player.addClass('vjs-nfb-next-episode')

    if (this.options.endingCreditsTimecode) {
      this.createNextEpisodeContainer()

      this.playerEl.insertBefore(this.els.nextEpisodeContainer, this.playerEl.querySelector('.vjs-control-bar'))
    }
    this.addListeners()
  }

  onTimeUpdate = throttle(() => {
    if (this.player?.currentTime() >= this.options.endingCreditsTimecode) {
      this.player.userActive(true)
      this.player.addClass('show-next-episode')
      this.showNextEpisode()
    } else {
      this.player.removeClass('show-next-episode')
      this.hideNextEpisode()
    }
  }, 500)

  addListeners () {
    this.player.on('timeupdate', this.onTimeUpdate)
    this.player.on('ended', this.showNextEpisode)
    this.els.nextEpisodeContainer.addEventListener('click', (e) => {
      this.dispatchNextEpisodeEvent(e)
    })
    this.els.nextEpisodeContainer.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' || e.code === 'Space') {
        this.dispatchNextEpisodeEvent(e)
      }
    })
  }

  // We need to check the state of this.isVisible. For instance, we only hide the next episode button when it's visible. Without this indicator, the animation to hide the button keeps getting triggered.

  showNextEpisode = () => {
    if (!this.isVisible) {
      this.revealBlock(this.els.nextEpisodeContainer, { slideRight: true })
      this.els.nextEpisodeContainer.style.pointerEvents = 'auto'
      this.isVisible = true
    }
  }

  hideNextEpisode = () => {
    if (this.isVisible) {
      this.hideBlock(this.els.nextEpisodeContainer, { slideLeft: true })
      this.els.nextEpisodeContainer.style.pointerEvents = 'none'
      this.isVisible = false
    }
  }

  dispatchNextEpisodeEvent = (clickEvent) => {
    clickEvent.target.dispatchEvent(new CustomEvent(CustomEvents.nextEpisodeClicked, { composed: true, bubbles: true }))
  }

  createNextEpisodeContainer () {
    this.log('Creating next episode container')
    this.els.nextEpisodeContainer = this.playerEl.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.nextEpisodeContainer, 'vjs-next-episode-container')
    this.els.nextEpisodeContainer.classList.add('nfb-button', 'nfb-button--primary', 'nfb-button--with-icon')
    this.els.nextEpisodeContainer.setAttribute('role', 'button')
    this.els.nextEpisodeContainer.tabIndex = 0
    this.els.nextEpisodeContainer.innerHTML = '<i class="nfb-icon icon-play"></i>' + this.player.localize('Next Episode')
    this.els.nextEpisodeContainer.style.opacity = '0'
    this.els.nextEpisodeContainer.style.pointerEvents = 'none'
  }

  revealBlock = (container, options = {}) => {
    if (container) {
      revealElement(container, options)
      if (options.forcePause && !this.player.paused()) {
        this.wasForcePausedBy = container
        this.player.pause()
      }
    }
  }

  hideBlock = (container, options = {}) => {
    if (container) {
      hideElement(container, options)
      if (this.wasForcePausedBy === container) {
        this.wasForcePausedBy = null
        this.player.play()
      }
    }
  }

  assignControlAttributes (element, controlName) {
    element.id = this.controlPrefix + controlName
    element.className = `${this.controlPrefix + controlName} ${controlName}`
  }
}

// Define default values for the plugin's `state` object here.
NFBNextEpisode.defaultState = {}

// Include the version number.
NFBNextEpisode.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbNextEpisode', NFBNextEpisode)

export default NFBNextEpisode
